import { HttpBackend, HttpClient } from '@angular/common/http';
import {
  Injectable,
  computed,
  inject,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  Observable,
  Subject,
  catchError,
  firstValueFrom,
  map,
  of,
  tap,
  throwError,
} from 'rxjs';

import { environment } from '../../../../environments/environment';
import { API_ENDPOINTS } from '../../constants/api-endpoints.const';
import { STORAGE_KEYS } from '../../constants/storage-keys.const';
import { StorageUtil } from '../../utils/storage.utils';
import {
  AccountType,
  parseAccountType,
} from '../models/account-type.enum';
import {
  ChangePasswordRequest,
  ForgotPasswordRequest,
  isLoginSuccessResponse,
  LoginRequest,
  LoginResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
  ResetPasswordRequest,
  TenantUser,
} from '../models/auth.model';
import {
  appPath,
  isUrlAllowedForAccountType,
  resolveAccountTypeFromUser,
  roleHomePath,
  rolePath,
} from '../../shared/routing/role-routing.util';
import { TenantService } from './tenant.service';
import { TokenService } from './token.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly refreshHttp = new HttpClient(inject(HttpBackend));
  private readonly router = inject(Router);
  private readonly tokenService = inject(TokenService);
  private readonly tenantService = inject(TenantService);

  private refreshCoalesced$: Observable<RefreshTokenResponse> | null = null;
  private logoutRequestStarted = false;

  private readonly userSignal = signal<TenantUser | null>(
    this.loadUserFromStorage(),
  );
  private readonly accountTypeSignal = signal<AccountType | null>(
    this.loadAccountTypeFromStorage(),
  );
  private readonly loadingSignal = signal(false);
  private readonly errorSignal = signal<string | null>(null);

  readonly user = this.userSignal.asReadonly();
  readonly accountType = this.accountTypeSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();

  readonly isAuthenticated = computed(() => !!this.userSignal());
  readonly userName = computed(() => {
    const user = this.userSignal();
    if (!user) {
      return '';
    }
     
    const displayName = user.display_name?.trim();

    if(displayName){
      return displayName;
    }

    const fullName = [user.first_name, user.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();

    return fullName || user.email;
  });

  readonly userFullName = computed(() => {
    const user = this.userSignal();
    if (!user) {
      return '';
    }
    const parts = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
    return parts ?? user.display_name?.trim() ?? user.email;
  });

  initAuthState(): void {
    this.checkAuthentication();
  }

  bootstrapSession(): Promise<void> {
    return firstValueFrom(this.restoreSession$()).catch(() => undefined);
  }

  /** Canonical role from login API / storage (single source of truth). */
  resolveAccountType(): AccountType | null {
    return (
      this.accountTypeSignal() ??
      resolveAccountTypeFromUser(
        this.userSignal()?.account_type,
        this.userSignal()?.role,
      ) ??
      this.tokenService.getAccountTypeFromToken()
    );
  }

  isReturnUrlAllowedForCurrentRole(returnUrl: string): boolean {
    const role = this.resolveAccountType();
    if (!role) {
      return false;
    }
    return isUrlAllowedForAccountType(
      returnUrl,
      role,
      this.tenantService.getTenantSlug(),
    );
  }

  login(credentials: LoginRequest): Observable<LoginResponse> {
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    return this.http
      .post<LoginResponse>(
        `${environment.apiUrl}${API_ENDPOINTS.AUTH.LOGIN}`,
        credentials,
      )
      .pipe(
        tap((response) => {
          this.handleLoginSuccess(response);
          this.loadingSignal.set(false);
        }),
        catchError((error) => {
          this.handleLoginError(error);
          this.loadingSignal.set(false);
          return throwError(() => error);
        }),
      );
  }

  logout(): void {
    if (this.logoutRequestStarted) {
      return;
    }
    this.logoutRequestStarted = true;
    this.loadingSignal.set(true);

    const refreshToken = this.tokenService.getRefreshToken() ?? '';

    this.http
      .post(`${environment.apiUrl}${API_ENDPOINTS.AUTH.LOGOUT}`, {
        refreshToken,
      })
      .subscribe({
        complete: () => {
          this.logoutRequestStarted = false;
          this.handleLogout();
        },
        error: () => {
          this.logoutRequestStarted = false;
          this.handleLogout();
        },
      });
  }

  refreshToken(): Observable<RefreshTokenResponse> {
    if (this.refreshCoalesced$) {
      return this.refreshCoalesced$;
    }

    const refreshToken = this.tokenService.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    const request: RefreshTokenRequest = { refreshToken };
    const bridge = new Subject<RefreshTokenResponse>();
    this.refreshCoalesced$ = bridge.asObservable();

    const tenantSlug = this.tenantService.getTenantSlug();
    const refreshHeaders: Record<string, string> = {};
    if (tenantSlug) refreshHeaders['x-tenant-name'] = tenantSlug;

    this.refreshHttp
      .post<RefreshTokenResponse>(
        `${environment.apiUrl}${API_ENDPOINTS.AUTH.REFRESH}`,
        request,
        { headers: refreshHeaders },
      )
      .subscribe({
        next: (response) => {
          this.tokenService.setToken(response.data.accessToken);
          if (response.data.refreshToken) {
            this.tokenService.setRefreshToken(response.data.refreshToken);
          }
          bridge.next(response);
          bridge.complete();
          this.refreshCoalesced$ = null;
        },
        error: (error: unknown) => {
          this.handleLogout();
          bridge.error(error);
          this.refreshCoalesced$ = null;
        },
      });

    return this.refreshCoalesced$;
  }

  forgotPassword(
    request: ForgotPasswordRequest,
  ): Observable<{ message: string }> {
    this.loadingSignal.set(true);
    return this.http
      .post<{ message: string }>(
        `${environment.apiUrl}${API_ENDPOINTS.AUTH.FORGOT_PASSWORD}`,
        request,
      )
      .pipe(
        tap(() => this.loadingSignal.set(false)),
        catchError((error) => {
          this.loadingSignal.set(false);
          return throwError(() => error);
        }),
      );
  }

  resetPassword(
    request: ResetPasswordRequest,
  ): Observable<{ message: string }> {
    this.loadingSignal.set(true);
    return this.http
      .post<{ message: string }>(
        `${environment.apiUrl}${API_ENDPOINTS.AUTH.RESET_PASSWORD}`,
        request,
      )
      .pipe(
        tap(() => this.loadingSignal.set(false)),
        catchError((error) => {
          this.loadingSignal.set(false);
          return throwError(() => error);
        }),
      );
  }

  changePassword(
    request: ChangePasswordRequest,
  ): Observable<{ message: string }> {
    this.loadingSignal.set(true);
    return this.http
      .post<{ message: string }>(
        `${environment.apiUrl}${API_ENDPOINTS.AUTH.CHANGE_PASSWORD}`,
        request,
      )
      .pipe(
        tap(() => this.loadingSignal.set(false)),
        catchError((error) => {
          this.loadingSignal.set(false);
          return throwError(() => error);
        }),
      );
  }

  /**
   * Returns the session user from memory or localStorage (login / profile PATCH).
   * No `/auth/me` call — that endpoint is not available yet.
   */
  getCurrentUser(): Observable<TenantUser> {
    const user = this.userSignal() ?? this.loadUserFromStorage();
    if (!user) {
      return throwError(() => new Error('No user session available'));
    }

    this.userSignal.set(user);
    this.persistAccountType(
      resolveAccountTypeFromUser(user.account_type, user.role) ??
        this.loadAccountTypeFromStorage(),
    );

    return of(user);
  }

  /** Updates session user after login or profile edits. */
  setSessionUser(user: TenantUser): void {
    this.userSignal.set(user);
    this.saveUserToStorage(user);
    const role = resolveAccountTypeFromUser(user.account_type, user.role);
    if (role) {
      this.persistAccountType(role);
    }
  }

  getRoleHomePath(accountType?: AccountType | null): string {
    const role = accountType ?? this.resolveAccountType();


    if (!role) {
      return '/auth/login';
    }

    return roleHomePath(role);
  }

  getRolePath(path: string, accountType?: AccountType | null): string {
    const role = accountType ?? this.resolveAccountType();
    if (!role) {
      return appPath(path);
    }
    return rolePath(path, role);
  }

  getMakerCasePath(batchId: string, caseId: string): string {
    return rolePath(`/case/${batchId}/${caseId}`, AccountType.MAKER);
  }

  getCheckerReviewPath(batchId: string, caseId: string): string {
    return rolePath(`/dashboard/checker/${batchId}/${caseId}`, AccountType.CHECKER);
  }

  /**
   * Navigate to the home route for the authenticated user's role.
   */
  navigateToRoleHome(accountType?: AccountType | null): void {
    void this.router.navigateByUrl(this.getRoleHomePath(accountType));
  }

  discardLocalSession(): void {
    this.tokenService.removeTokens();
    this.userSignal.set(null);
    this.accountTypeSignal.set(null);
    StorageUtil.removeLocal(STORAGE_KEYS.USER_DATA);
    StorageUtil.removeLocal(STORAGE_KEYS.ACCOUNT_TYPE);
    this.errorSignal.set(null);
  }

  private handleLoginSuccess(response: LoginResponse): void {
    if (!isLoginSuccessResponse(response)) {
      this.errorSignal.set(response.message || 'Login failed. Please try again.');
      return;
    }

    const user = response.data.user;

    const role =
      resolveAccountTypeFromUser(user.account_type, user.role) ??
      this.tokenService.getAccountTypeFromToken(response.data.accessToken);

    if (!role) {
      this.errorSignal.set('Access denied for this account type.');
      return;
    }

    this.tokenService.setToken(response.data.accessToken);
    this.tokenService.setRefreshToken(response.data.refreshToken);
    this.userSignal.set(user);
    this.saveUserToStorage(user);
    this.persistAccountType(role);
    this.errorSignal.set(null);
  }

  private handleLoginError(error: {
    errorMessage?: string;
    error?: { message?: string | string[] };
  }): void {
    const raw =
      error.errorMessage ??
      error.error?.message ??
      'Login failed. Please try again.';
    const errorMessage = Array.isArray(raw) ? raw.join(', ') : String(raw);
    this.errorSignal.set(errorMessage);
  }

  private handleLogout(): void {
    this.discardLocalSession();
    this.loadingSignal.set(false);
    void this.router.navigate(['/auth/login']);
  }

  private persistAccountType(role: AccountType | null): void {
    this.accountTypeSignal.set(role);
    if (role) {
      StorageUtil.setLocal(STORAGE_KEYS.ACCOUNT_TYPE, role);
    } else {
      StorageUtil.removeLocal(STORAGE_KEYS.ACCOUNT_TYPE);
    }
  }

  private saveUserToStorage(user: TenantUser): void {
    StorageUtil.setLocal(STORAGE_KEYS.USER_DATA, user);
  }

  private loadUserFromStorage(): TenantUser | null {
    return StorageUtil.getLocal<TenantUser>(STORAGE_KEYS.USER_DATA);
  }

  private loadAccountTypeFromStorage(): AccountType | null {
    const stored = StorageUtil.getLocal<string>(STORAGE_KEYS.ACCOUNT_TYPE);
    return parseAccountType(stored);
  }

  private checkAuthentication(): void {
    this.restoreSession$().subscribe();
  }

  private restoreSession$(): Observable<void> {
    const hasToken = this.tokenService.hasToken();
    const isExpired = this.tokenService.isTokenExpired();

    if (hasToken && !isExpired) {
      const user = this.loadUserFromStorage();
      const role = this.loadAccountTypeFromStorage();
      if (user) {
        this.userSignal.set(user);
      }
      if (role) {
        this.accountTypeSignal.set(role);
      } else if (user) {
        const resolved = resolveAccountTypeFromUser(user.account_type, user.role);
        if (resolved) {
          this.persistAccountType(resolved);
        }
      }
      return of(undefined);
    }

    if (hasToken && isExpired) {
      return this.refreshToken().pipe(
        map(() => undefined),
        catchError(() => {
          this.handleLogout();
          return of(undefined);
        }),
      );
    }

    this.userSignal.set(null);
    this.accountTypeSignal.set(null);
    return of(undefined);
  }
}
